import { memo, forwardRef } from "react";
import DatePicker from "react-datepicker";
import clsx from "clsx";
import "react-datepicker/dist/react-datepicker.css";
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import styles from "./DatePicker.module.scss";

interface IDatePickerProps {
  rightLabel?: string;
  leftLabel?: string;
  selected: Date | null;
  handleChange?: (date: Date | null) => void;
  handleChangeRange?: (date: [Date | null, Date | null]) => void;
  includedDates?: Date[];
  className?: string;
  iconClassName?: string;
  startDate?: Date | null;
  endDate?: Date | null;
  maxDate?: Date;
  selectsRange?: true;
  disabled?: boolean;
  isClearable?: boolean;
}

export const CustomDatePicker = memo(
  ({
    rightLabel,
    leftLabel,
    selected,
    handleChange,
    handleChangeRange,
    includedDates,
    className,
    iconClassName,
    startDate,
    endDate,
    maxDate,
    selectsRange,
    disabled = false,
    isClearable = false,
  }: IDatePickerProps) => {
    // Custom calendar button shown instead of default input field
    const CalendarButton = forwardRef<HTMLDivElement, { onClick?: () => void }>(
      ({ onClick }, ref) => (
        <div
          ref={ref}
          onClick={onClick}
          className={clsx("date-picker-button", className)}
          style={{
            padding: "5px 10px",
            border: "1px solid rgb(var(--border-secondary))",
            backgroundColor: "rgb(var(--surface-1))",
            cursor: "pointer",
            fontSize: "12px",
            fontWeight: "500",
            height: "34px",
            color: "rgb(var(--text-primary))",
            borderRadius: "8px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            minWidth: "200px",
            // Remove inline styles that might override CSS classes
            // Let CSS handle the styling based on active class
          }}
        >
          <span>{leftLabel || "Date Range"}</span>
          <CalendarMonthIcon className={iconClassName} />
        </div>
      )
    );

    // When disabled, show only the button (no datepicker popup)
    if (disabled) {
      return <CalendarButton />;
    }

    return (
      <div className={styles.calendar}>
        <DatePicker
          selected={selected}
          onChange={selectsRange ? handleChangeRange : handleChange}
          startDate={startDate}
          endDate={endDate}
          selectsRange={selectsRange}
          customInput={<CalendarButton />}
          maxDate={maxDate}
          includeDates={includedDates}
          isClearable={isClearable}
          placeholderText="Select date range"
          dateFormat="MM/dd/yyyy"
          shouldCloseOnSelect={true}
          onCalendarClose={() => {
            // Calendar will close automatically when both dates are selected
            // or when user clicks outside
          }}
        />
      </div>
    );
  }
);

CustomDatePicker.displayName = "CustomDatePicker";